import React, {useEffect, useState} from "react";
import {FormControl, MenuItem, Paper, Select} from "@mui/material";
import {
  DataGridPro,
  gridClasses, useGridApiRef,
} from "@mui/x-data-grid-pro";
import {csCZ} from "@mui/x-data-grid/locales";
import Typography from "@mui/material/Typography";
import {bankColumns} from "../data/DatatableColumns";
import {convertDateToMMYY} from "../utils/Coverter";
import {filterBankList} from "../utils/Filter";
import {generateHiddenColumns} from "../utils/ValueGenerator";

const columnGroupingModel = (months) => [
  ...months.map((month) => ({
    groupId: month,
    headerName: month.slice(0, 2) + "/" + month.slice(2),
    headerAlign: "center",
    headerClassName: `spendings`,
    children: [
      {field: `spendings_${month}`, headerName: "Spendings"},
      {field: `assumptions_${month}`, headerName: "Assumptions"},
    ],
  })),
  {
    groupId: 'sum',
    headerName: 'Suma za období',
    headerAlign: 'center',
    headerClassName: `spendings`,
    children: [
      {field: 'sum', headerName: 'sum'},
      {field: 'sumAssumption', headerName: 'Total Assumption'},
    ],
  },
  {
    groupId: 'total',
    headerName: 'Suma celkem',
    headerAlign: 'center',
    children: [
      {field: 'total', headerName: 'sum'},
      {field: 'totalAssumption', headerName: 'Total Assumption'},
    ],
  },
];

const returnCashflow = (data, months) => {
  const numMonths = months.length;
  let sumOutcomes = 0;
  let sumIncomes = 0;
  let totalOutcomes = 0;
  let totalIncomes = 0;
  let incomesArray = Array(numMonths).fill(0);
  let outcomesArray = Array(numMonths).fill(0);

  const invoiceList = [
    ...data.report.receivedInvoiceList,
    ...data.report.commitmentList,
  ];
  invoiceList.forEach(invoice => {
    const payments = invoice.paymentList.length ? invoice.paymentList : [{
      paymentDate: invoice.supplyDate || invoice.dueDate,
      paymentAmount: invoice.actualPayment || 0
    }];

    payments.forEach(payment => {
      const monthYear = convertDateToMMYY(payment.paymentDate);
      const monthIndex = months.indexOf(monthYear);

      if (monthIndex !== -1) {
        outcomesArray[monthIndex] -= payment.paymentAmount;
        sumOutcomes -= payment.paymentAmount;
      }
      totalOutcomes -= payment.paymentAmount;
    });
  });

  data.report.claimList.forEach(claim => {
    claim.paymentList.forEach(payment => {
      const monthYear = convertDateToMMYY(payment.paymentDate);
      const monthIndex = months.indexOf(monthYear);
      const actualPayment = payment.paymentAmount || 0;
      if (monthIndex !== -1) {
        incomesArray[monthIndex] += actualPayment;
        sumIncomes += actualPayment;
      }
      totalIncomes += actualPayment;
    });
  });

  return {
    sumOutcomes,
    sumIncomes,
    totalOutcomes,
    totalIncomes,
    incomesArray,
    outcomesArray
  };
};

const compareDates = (date1, date2) => {
  const month1 = parseInt(date1.slice(0, 2), 10);
  const year1 = parseInt(date1.slice(2, 4), 10);
  const month2 = parseInt(date2.slice(0, 2), 10);
  const year2 = parseInt(date2.slice(2, 4), 10);

  if (year1 > year2) {
    return 1;
  } else if (year1 < year2) {
    return -1;
  } else {
    if (month1 > month2) {
      return 1;
    } else if (month1 < month2) {
      return -1;
    } else {
      return 0;
    }
  }
}

const calculateSum = (index, rowDefinitions, spendings) => {
  const accountBalanceIndex = rowDefinitions.findIndex(row => row.code === "accountBalance");

  if (index === accountBalanceIndex) {
    return '-';
  } else {
    return spendings[index].reduce((sum, value) => sum + value, 0);
  }
}

const calculateTotal = (index, rowDefinitions, total) => {
  const outcomeIndex = rowDefinitions.findIndex(row => row.code === "vydej");
  const accountBalanceIndex = rowDefinitions.findIndex(row => row.code === "accountBalance");
  switch (index) {
    case outcomeIndex:
      return -total[index];
    case accountBalanceIndex:
      return '-';
    default:
      return total[index];
  }
}

const addRowIfNotExists = (rowDefinitions, code, name) => {
  const rowExists = rowDefinitions.some(row => row.code === code);
  if (!rowExists) {
    rowDefinitions.push({code, name});
  }
}

const generateRows = (data, months, rowDefinitions, selectedBank) => {
  addRowIfNotExists(rowDefinitions, "difference", "Rozdíl");
  addRowIfNotExists(rowDefinitions, "allAccountOutcomes", "Rozdíl všech účtů (výdej)");
  addRowIfNotExists(rowDefinitions, "allAccountIncomes", "Rozdíl všech účtů (příjem)");
  addRowIfNotExists(rowDefinitions, "accountBalance", "Stav účtu");
  addRowIfNotExists(rowDefinitions, "accountingOutcomes", "Účetní rozdíl (Výdej)");
  addRowIfNotExists(rowDefinitions, "accountingIncomes", "Účetní rozdíl (Příjem)");
  addRowIfNotExists(rowDefinitions, "uncategorized", "K zařazení");

  const numMonths = months.length;
  const spendings = rowDefinitions.map(() => {
    return Array(numMonths).fill(0);
  });

  const assumptions = rowDefinitions.map((rowDef) => {
    return Array(numMonths).fill(rowDef.code === "accountBalance" || rowDef.code === "uncategorized" ? "-" : 0);
  })

  const total = rowDefinitions.map(() => 0);

  months && months.length > 0 && data.report.bankList.forEach(bankData => {
    const monthYear = convertDateToMMYY(bankData.expoDate);
    const monthIndex = months.indexOf(monthYear);
    const rowIndex = rowDefinitions.findIndex(row => bankData.movementType === row.code);
    if (compareDates(monthYear, months[0]) < 0) {
      for (let i = 0; i < numMonths; i++) {
        if (selectedBank === "Souhrn" || selectedBank === bankData.bankAccount) spendings[rowDefinitions.findIndex(row => row.code === "accountBalance")][i] += bankData.movementType === "prijem" ? bankData.amount : -bankData.amount;
      }
    }
    if (monthIndex !== -1 && rowIndex !== -1) {
      if (selectedBank === "Souhrn" || selectedBank === bankData.bankAccount) spendings[rowIndex][monthIndex] += bankData.movementType === "prijem" ? bankData.amount : -bankData.amount;
      if (bankData.movementType === "vydej") {
        if (selectedBank === "Souhrn" || selectedBank === bankData.bankAccount) spendings[rowDefinitions.findIndex(row => row.code === "difference")][monthIndex] -= bankData.amount;
        spendings[rowDefinitions.findIndex(row => row.code === "allAccountOutcomes")][monthIndex] -= bankData.amount;
        for (let i = monthIndex; i < numMonths; i++) {
          if (selectedBank === "Souhrn" || selectedBank === bankData.bankAccount) spendings[rowDefinitions.findIndex(row => row.code === "accountBalance")][i] -= bankData.amount;
        }
      } else if (bankData.movementType === "prijem") {
        if (selectedBank === "Souhrn" || selectedBank === bankData.bankAccount) spendings[rowDefinitions.findIndex(row => row.code === "difference")][monthIndex] += bankData.amount;
        spendings[rowDefinitions.findIndex(row => row.code === "allAccountIncomes")][monthIndex] += bankData.amount;
        for (let i = monthIndex; i < numMonths; i++) {
          if (selectedBank === "Souhrn" || selectedBank === bankData.bankAccount) spendings[rowDefinitions.findIndex(row => row.code === "accountBalance")][i] += bankData.amount;
        }
      }
    }
    if (bankData.movementType === "vydej") {
      if (selectedBank === "Souhrn" || selectedBank === bankData.bankAccount) total[rowDefinitions.findIndex(row => row.code === "difference")] -= bankData.amount;
      total[rowDefinitions.findIndex(row => row.code === "allAccountOutcomes")] -= bankData.amount;
    } else if (bankData.movementType === "prijem") {
      if (selectedBank === "Souhrn" || selectedBank === bankData.bankAccount) total[rowDefinitions.findIndex(row => row.code === "difference")] += bankData.amount;
      total[rowDefinitions.findIndex(row => row.code === "allAccountIncomes")] += bankData.amount;
    }
    total[rowIndex] += bankData.amount;
  });

  const filteredBankList = filterBankList(data.report.bankList, data.report.companyDetail.code);

  if (months?.length) {
    const uncategorizedIndex = rowDefinitions.findIndex(row => row.code === "uncategorized");

    filteredBankList.forEach(bankData => {
      const monthYear = convertDateToMMYY(bankData.expoDate);
      const monthIndex = months.indexOf(monthYear);
      const isSelectedBank = selectedBank === "Souhrn" || selectedBank === bankData.bankAccount;
      const isUncategorized = bankData.relationList.length === 0;
      const amount = isUncategorized ? bankData.amount : 0;

      if (monthIndex !== -1 && isSelectedBank && isUncategorized) {
        if (bankData.movementType === "vydej") {
          spendings[uncategorizedIndex][monthIndex] -= amount;
        } else if (bankData.movementType === "prijem") {
          spendings[uncategorizedIndex][monthIndex] += amount;
        }
      }

      if (isSelectedBank && isUncategorized) {
        if (bankData.movementType === "vydej") {
          total[uncategorizedIndex] -= amount;
        } else if (bankData.movementType === "prijem") {
          total[uncategorizedIndex] += amount;
        }
      }
    });
  }
  spendings[rowDefinitions.findIndex(row => row.code === "accountingOutcomes")] = spendings[rowDefinitions.findIndex(row => row.code === "allAccountOutcomes")].map((value, index) => value - spendings[rowDefinitions.findIndex(row => row.code === "allAccountOutcomes")][index]);
  spendings[rowDefinitions.findIndex(row => row.code === "accountingIncomes")] = spendings[rowDefinitions.findIndex(row => row.code === "allAccountIncomes")].map((value, index) => value - spendings[rowDefinitions.findIndex(row => row.code === "allAccountIncomes")][index]);

  const {
    totalOutcomes,
    totalIncomes,
    incomesArray,
    outcomesArray
  } = returnCashflow(data, months);

  for (let i = 0; i < numMonths; i++) {
    spendings[rowDefinitions.findIndex(row => row.code === "accountingOutcomes")][i] = Math.abs(spendings[rowDefinitions.findIndex(row => row.code === "allAccountOutcomes")][i]) - Math.abs(outcomesArray[i]);
    spendings[rowDefinitions.findIndex(row => row.code === "accountingIncomes")][i] = spendings[rowDefinitions.findIndex(row => row.code === "allAccountIncomes")][i] - incomesArray[i];
  }
  total[rowDefinitions.findIndex(row => row.code === "accountingOutcomes")] = Math.abs(Math.abs(total[rowDefinitions.findIndex(row => row.code === "allAccountOutcomes")]) - Math.abs(totalOutcomes));
  total[rowDefinitions.findIndex(row => row.code === "accountingIncomes")] = Math.abs(Math.abs(total[rowDefinitions.findIndex(row => row.code === "allAccountIncomes")]) - Math.abs(totalIncomes));

  // Calculate assumptions

  data.report.contractList.forEach(contract => {
    contract.userValueList.forEach(userValue => {
      userValue.businessMonthAssumptionList.forEach(assumptionEntry => {
        const monthIndex = months.indexOf(assumptionEntry.businessMonth);
        const value = assumptionEntry.value;
        if (monthIndex !== -1) {
          assumptions[rowDefinitions.findIndex(row => row.code === "vydej")][monthIndex] -= value;
          assumptions[rowDefinitions.findIndex(row => row.code === "difference")][monthIndex] -= value;
        }
      });
    });
  });

  data.report.claimList.forEach(claim => {
    const assumptionMonthYear = convertDateToMMYY(claim.dueDate);
    const assumptionMonthIndex = months.indexOf(assumptionMonthYear);
    const amountTotal = claim?.amountTotal || 0;

    if (assumptionMonthIndex !== -1) {
      assumptions[rowDefinitions.findIndex(row => row.code === "prijem")][assumptionMonthIndex] += claim.sourceList.includes("DPH") ? 0 : amountTotal;
      assumptions[rowDefinitions.findIndex(row => row.code === "difference")][assumptionMonthIndex] += amountTotal;
    }

    const dphMonthYear = convertDateToMMYY(claim.accountingDate);
    const dphMonthIndex = months.indexOf(dphMonthYear);
    if (dphMonthIndex !== -1) {
      assumptions[rowDefinitions.findIndex(row => row.code === "vydej")][dphMonthIndex] -= claim.amountVat;
      assumptions[rowDefinitions.findIndex(row => row.code === "difference")][dphMonthIndex] -= claim.amountVat;
    }
  });

  const invoiceList = [
    ...data.report.receivedInvoiceList,
    ...data.report.commitmentList,
  ];
  invoiceList.forEach(invoice => {
    const dphMonthYear = convertDateToMMYY(invoice.supplyDate);
    const dphMonthIndex = months.indexOf(dphMonthYear);
    if (dphMonthIndex !== -1) {
      assumptions[rowDefinitions.findIndex(row => row.code === "prijem")][dphMonthIndex] += invoice.amountVat;
      assumptions[rowDefinitions.findIndex(row => row.code === "difference")][dphMonthIndex] += invoice.amountVat;
    }
  });

  return rowDefinitions.map((rowDef, index) => {
    const sum = calculateSum(index, rowDefinitions, spendings);
    const totalValue = calculateTotal(index, rowDefinitions, total);

    return {
      id: index,
      label: rowDef.name,
      spendings: spendings[index],
      assumptions: assumptions[index],
      sum: sum,
      total: totalValue,
    };
  });
};

function extractBankAccounts(data) {
  const bankAccounts = new Set();

  data.forEach(entry => {
    if (entry.bankAccount) {
      bankAccounts.add(entry.bankAccount);
    }
  });

  return ["Souhrn", ...Array.from(bankAccounts)];
}

const BankTable = ({data, months, rowDefinitions, reference, onRowsChange}) => {
  const allBanks = months && rowDefinitions && extractBankAccounts(data.report.bankList);
  const [selectedBank, setSelectedBank] = useState(allBanks[0]);
  const [rows, setRows] = useState(generateRows(data, months, rowDefinitions, selectedBank));
  const [hiddenColumns, setHiddenColumns] = useState({});
  const apiRef = useGridApiRef();

  useEffect(() => {
    setRows(generateRows(data, months, rowDefinitions, selectedBank));
    const newHiddenColumns = generateHiddenColumns(months);
    setHiddenColumns(newHiddenColumns);

    if (apiRef.current) {
      apiRef.current.setState((prevState) => ({
        ...prevState,
        columns: {
          ...prevState.columns,
          columnVisibilityModel: newHiddenColumns,
        },
      }));
      apiRef.current.forceUpdate();
    }

    onRowsChange && onRowsChange();
  }, [months, data, rowDefinitions, onRowsChange]);

  console.log("BankRows", rows);

  const pinnedRows = {
    bottom: [
      rows[rowDefinitions.findIndex(row => row.code === "difference")],
      rows[rowDefinitions.findIndex(row => row.code === "accountBalance")],
      //rows[rowDefinitions.findIndex(row => row.code === "accountingOutcomes")],
      //rows[rowDefinitions.findIndex(row => row.code === "accountingIncomes")],
      rows[rowDefinitions.findIndex(row => row.code === "uncategorized")],
    ]
  };

  return (
    data.report && months.length > 0 && rowDefinitions.length > 0 &&
    <Paper ref={reference}>
      <Typography
        sx={{
          paddingLeft: 2,
          paddingTop: 1,
          paddingBottom: 1,
          fontWeight: 'bold',
          backgroundColor: 'white',
          position: 'sticky',
          left: 0,
          fontSize: "0.85rem",
          alignItems: 'center',
          display: 'flex',
        }}
      >
        Banka
        <FormControl>
          <Select
            value={selectedBank}
            onChange={(e) => setSelectedBank(e.target.value)}
            sx={{width: "auto", ml: 3, mt: 0.7}}
            size="small"
            variant="standard"
          >
            {allBanks.map((bank) => (
              <MenuItem key={bank} value={bank}>
                {bank}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Typography>

      <DataGridPro
        apiRef={apiRef}
        rows={rows.filter(row => !(row.label.startsWith("Rozdíl všech účtů") || row.label.startsWith("Účetní rozdíl")))}
        hideFooter
        disableRowSelectionOnClick
        pinnedRows={pinnedRows}
        columns={bankColumns(months)}
        columnGroupingModel={columnGroupingModel(months)}
        localeText={csCZ.components.MuiDataGrid.defaultProps.localeText}
        initialState={{
          pinnedColumns: {
            left: ['label'],
            right: ['sum', 'total'],
          },
          columns: {
            columnVisibilityModel: {
              ...hiddenColumns,
            },
          }
        }}
        disableSelectionOnClick
        getCellClassName={(params) => {
          let classNames = '';

          if (params.value < 0) {
            classNames += 'negative ';
          }

          if (params.rowNode.type === 'pinnedRow') {
            if (params.colDef.headerName.includes('Skutečnost')) {
              classNames += 'pinned-row_spendings ';
            } else {
              classNames += 'pinned-row ';
            }
          }
          classNames += 'spendings ';


          return classNames.trim();
        }}
        sx={{
          '&.MuiDataGrid-root': {
            borderRadius: '0px',
            borderLeft: 'none',
            borderRight: 'none',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
            overflow: 'visible',
          },
          '& .MuiDataGrid-columnHeader': {borderRight: '1px solid rgba(224, 224, 224, 1)'},
          '& .spendings': {
            borderRight: '1px solid rgba(224, 224, 224, 1)',
          },
          [`.${gridClasses.cell}.pinned-row`]: {
            fontWeight: 'bold',
            backgroundColor: '#f5f5f5',
          },
          [`.${gridClasses.cell}.pinned-row_spendings`]: {
            fontWeight: 'bold',
            backgroundColor: '#f5f5f5',
            borderRight: '1px solid rgba(224, 224, 224, 1)',
          },
          '& .MuiDataGrid-cell.negative': {
            color: 'red',
          },
        }}
      />
    </Paper>
  );
};

export default BankTable;